/**
 * Customer-360 header — account name + number, Zoom's Account type (display only — the API's
 * account_type_display; since the 2026-09-18 owner ruling the Overview card shows this SAME field, not
 * the plan name — the real plan (base_type) still drives the Overview's Plan status pill, TierBadge and
 * PlanFreezeNotice, and the Subscription tab), seats, renewal date, and status. It reuses the overview
 * query (same cache key) so it never double-fetches, and shows a skeleton/error inline so the header is
 * honest while the page loads.
 */

import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui";
import { RetiredBadge } from "@/components/portal/RetiredBadge";
import { AccountStatusBadge } from "@/components/portal/AccountStatusBadge";
import { ActivationBadge } from "@/components/portal/ActivationBadge";
import { useCustomerActivation } from "@/features/activation/useActivation";
import { isZombieFreeAccount, showsRenewal } from "@/lib/accountStatus";
import { formatDate, formatInt } from "@/lib/format";
import type { CustomerOverview } from "@/api/types";
import { useCustomerOverview } from "./useCustomer";

export function CustomerHeader({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const overview = useCustomerOverview(accountId);
  // Its own query + polling contract (features/activation/useActivation.ts) — never folded into the
  // overview, which is mirror data on a 60 s staleTime.
  const activation = useCustomerActivation(accountId);

  if (overview.isPending) {
    return (
      <header className="flex flex-col gap-2">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-4 w-96" />
      </header>
    );
  }

  // The header degrades gracefully on error; the tab body shows the full error state.
  const d: Partial<CustomerOverview> = overview.data ?? {};
  const title = d.account_name ?? d.account_number ?? accountId;

  // `isZombieFree` (lib/accountStatus.ts::isZombieFreeAccount — SHARED with OverviewTab's Kpis, round 2
  // W2) detects when the server ruling's `free` verdict OVERRODE what plan_state/base_type alone would
  // have said (QUALICA, G STEEL, GJ Steel — all really "Free (with credit card)" on account_type, but
  // still carrying a real historical base plan): seats/seats_last_known/renewal_date below are then
  // ZOMBIE — leftovers of a plan the ruling says does not apply. An account that already read Free under
  // the OLD collapse (no base row at all — the ordinary ~892) is not zombie, and today's rendering for
  // that shape is untouched.
  const isZombieFree = isZombieFreeAccount(d.plan_state, d.base_type, d.plan_badge);
  // W8-Q1 (owner ruling 2026-09-19): a Free account shows NO Renewal — broader than isZombieFree on
  // purpose (see lib/accountStatus.ts::showsRenewal); seats keep the narrower zombie gate.
  const renewalShown = showsRenewal(d.plan_state, d.base_type, d.plan_badge);

  return (
    <header className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <h1 className="text-(length:--text-2xl) font-semibold text-(--text-strong)">{title}</h1>
        {/* ONE derived status — Active / Free / Expired / Unknown (lib/accountStatus.ts). It replaces
            BOTH the old Zoom raw `account_status` pill (which read "active" even on a long-dead plan) and
            the seven-way plan_state badge with its essay tooltips. base_type feeds the Free-vs-Unknown
            split (no plan on record → Free; an opaque code → Unknown). */}
        <AccountStatusBadge
          plan_state={d.plan_state}
          base_type={d.base_type}
          plan_badge={d.plan_badge}
        />
        {/* A DIFFERENT AXIS from the plan status — "Zoom stopped listing this account" is independent of
            whether its plan is live/expired, so this badge renders alongside, never suppressed. Someone
            navigated HERE on purpose, so we badge rather than hide; full form (with the date) fits here. */}
        <RetiredBadge is_active={d.is_active} left_list_at={d.left_list_at} />
        {/* A THIRD axis: has the owner account signed in (ADR ACTIVATION-BADGE). Rendered once its own
            query settles, so it never flashes "Activate: Unknown" on load. An error with nothing cached
            (unknown account, or an API image without the route) renders "Activate: Unknown" — the header
            never blanks. A failing REFETCH over older data keeps the label but drops to neutral + "not
            updating". */}
        {!activation.isPending && (
          <ActivationBadge
            activation={activation.data}
            refetchFailing={activation.isRefetchError}
          />
        )}
      </div>
      <dl className="flex flex-wrap items-center gap-x-3 gap-y-2 text-(length:--text-sm)">
        {d.account_number && <Fact label={t("customer.accountNumber")} value={d.account_number} />}
        {/* N0-4 · Zoom's "Account Type" — what its Customer List prints, NOT the plan: the API sends
            account_type_display (account_type, with 'Business' on an Enterprise-family tier read as
            'Enterprise'). DISPLAY ONLY: account_type is a decoy for entitlement, so nothing here or
            anywhere branches on it — badge, tier and seats stay on base_type. OWNER RULING (2026-09-18):
            the Overview card renders this same account_type_display now, not a plan name — the real plan
            (base_type) still drives that card's Plan status pill, TierBadge and PlanFreezeNotice. */}
        {d.account_type_display && (
          <Fact label={t("customer.accountType")} value={d.account_type_display} />
        )}
        {/* THE HEADER'S OLD LIE. It printed "Business · 15 seats" for ISALAMIC — an account Zoom's own
            console reports as Basic with ZERO assignable licences — because `seats` was a leftover trial
            figure Zoom never cleans up.
            The backend now NULLs `seats` the moment the plan is provably not live and moves the frozen
            number to `seats_last_known`. So:
              plan live      -> "Seats: 15"                  (a current fact)
              plan not live  -> "Seats (last known): 15"     (a historical fact, LABELLED as one)
            Blanking it entirely would destroy exactly the figure a win-back or renewal-recovery call
            needs ("how big were they?"); leaving it under "Seats" presents a dead number as current. A
            clearly-labelled historical figure is the only option that is both honest AND useful.
            The label carries the warning on its own — the `hint` tooltip that used to restate it in a
            longer sentence is deleted. `muted` still keeps the frozen figure visually distinct.
            ⚠️ The two SEATS facts below are additionally gated on `!isZombieFree` (API 0030), and the
            Renewal fact on the BROADER `renewalShown` (any Free account — W8-Q1, 2026-09-19): a `free`
            verdict that OVERRODE a real plan_state/base_type reading means these are leftovers of a plan
            the ruling says does not apply — a current-looking "Seats: 50" or a renewal date beside a
            "Free" pill would be exactly the zombie-plan lie this header exists to kill. This is a
            DIFFERENT axis from the not-live freeze above: PlanFreezeNotice / isPlanNotLive are untouched,
            on purpose (the two can disagree — an OPEN product question, not resolved here).
            Round 2 (W2): OverviewTab's Kpis gates its OWN Renewal tile and Seats / "Seats (last known)"
            tile on the same shared `isZombieFreeAccount` — this header no longer being the only place
            these figures could leak. */}
        {/* `!d.no_meeting_plan` (N0-4): a Zoom-Phone-only account has no meeting plan, and its seat
            figure is a 0 default, never a size ("Seats (last known) 0" said nothing true). The API sets
            the flag from the PLANCODE_7200/7201 placeholder base only, so every other cohort — including
            the not-live ones that DO carry a real last-known figure — renders exactly as before. */}
        {!isZombieFree && !d.no_meeting_plan && d.seats != null && (
          <Fact label={t("customer.seats")} value={formatInt(d.seats)} />
        )}
        {!isZombieFree && !d.no_meeting_plan && d.seats == null && d.seats_last_known != null && (
          <Fact label={t("planState.seatsLastKnown")} value={formatInt(d.seats_last_known)} muted />
        )}
        {renewalShown && d.renewal_date && (
          <Fact
            label={t("customer.renewal")}
            value={
              d.days_until_renewal != null
                ? `${formatDate(d.renewal_date)} · ${t("customer.daysUntilRenewal", { days: d.days_until_renewal })}`
                : formatDate(d.renewal_date)
            }
          />
        )}
      </dl>
    </header>
  );
}

/**
 * A labelled fact in the meta row, separated by a hairline pipe between siblings.
 *
 * `muted` exists for the LAST-KNOWN figures: a frozen number must not carry the same visual weight as a
 * live one. It used to have a `hint` tooltip too, for the same figures — deleted with the copy cut, and
 * the prop deleted with it rather than left behind as a prop nothing passes.
 */
function Fact({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="inline-flex items-center gap-1.5 border-l border-(--border) pl-3 first:border-0 first:pl-0">
      <dt className="text-(--text-muted)">{label}</dt>
      <dd className={muted ? "font-medium text-(--text-muted)" : "font-medium text-(--text)"}>
        {value}
      </dd>
    </div>
  );
}
